import React, { useState } from 'react'
import { connect } from 'react-redux'
import { mapLanguageToComponentProps } from 'i18n'
import styled from 'styled-components'
import { I18nComponentType } from 'i18n-types'
import {
  Small,
  PrimaryLinkStyle,
  PrimaryLinkStyleButton,
  Body,
} from 'components/styled/typography'
import { Flex } from 'components/styled/containers/flex'
import { Icon } from 'components/styled/icon'
import { BodyStyle, SmallStyle } from 'components/styled/typography/styles'
import { ThemeColor } from 'theme/colors'
import ActionRemove from 'components/action-remove'
import { size } from 'lodash'

interface ConfirmDeleteProps {
  i18n: I18nComponentType
  onConfirm: () => void
  size?: 'small' | 'default'
  customDeleteText?: string
  showDeleteText?: boolean
  children?: React.ReactNode | React.ReactNode[]
  color?: ThemeColor
}

export const ConfirmBox = styled(Flex)`
  align-items: center;
  gap: ${({ theme }) => theme.space.s8};
  min-height: ${({ theme }) => theme.space.s32};
  flex-wrap: wrap;
`

const CancelButton = styled(PrimaryLinkStyleButton)<{
  $size: 'small' | 'default'
}>`
  ${({ $size }) => ($size === 'small' ? SmallStyle : BodyStyle)};
  margin-bottom: 0;
  && {
    color: ${({ theme }) => theme.color.grey90};

    &:hover {
      color: ${({ theme }) => theme.color.grey90};
    }
  }
`

const ConfirmText = styled.span<{
  $size: 'small' | 'default'
}>`
  ${PrimaryLinkStyle};
  ${({ $size }) => ($size === 'small' ? SmallStyle : BodyStyle)};
  && {
    margin-bottom: 0;
  }
`

const ConfirmButton = styled.button`
  display: flex;
  align-items: center;

  &:hover,
  &:active {
    ${ConfirmText} {
      text-decoration: underline;
    }
  }
`

const StyledConfirmDelete = styled(Flex)`
  line-height: inherit;
  flex-wrap: wrap;
`

// Confirm delete that looks like basic secondary button
export const ConfirmDeleteFC = ({
  i18n,
  onConfirm,
  size = 'default',
  customDeleteText,
  showDeleteText = false,
  children,
  color = 'grey90',
}: ConfirmDeleteProps) => {
  const [confirmDelete, setConfirmDelete] = useState(false)

  const onCancelClick = () => {
    setConfirmDelete(false)
  }

  const onConfirmClick = () => {
    setConfirmDelete(false)
    onConfirm()
  }

  const onOpenConfirm = () => {
    setConfirmDelete(true)
  }

  const confirmText = children || `${i18n.confirmDelete.delete}?`
  const buttonText = customDeleteText || i18n.confirmDelete.delete
  const TextElement = size === 'small' ? Small : Body

  return (
    <StyledConfirmDelete alignItems="center">
      {!confirmDelete && (
        <ActionRemove color={color} onClick={() => onOpenConfirm()}>
          {showDeleteText ? buttonText : ''}
        </ActionRemove>
      )}
      {confirmDelete && (
        <ConfirmBox alignItems="center">
          <TextElement mb="s0" fontWeight={600}>
            {confirmText}
          </TextElement>
          <Flex alignItems="center">
            <CancelButton $size={size} onClick={() => onCancelClick()}>
              {i18n.confirmDelete.cancel}
            </CancelButton>
            <ConfirmButton onClick={() => onConfirmClick()}>
              <ConfirmText $size={size}>
                {i18n.confirmDelete.confirm}
              </ConfirmText>
            </ConfirmButton>
          </Flex>
        </ConfirmBox>
      )}
    </StyledConfirmDelete>
  )
}

export const ConfirmDelete = connect(mapLanguageToComponentProps())(
  ConfirmDeleteFC
)
